/**
 * 현장 자재 API 클라이언트.
 * 백엔드 /v0.1/site-management/projects/:id/materials + /materials/:id 와 1:1 매칭.
 */
import { API_ENDPOINTS } from '../config/api';
import { apiClient } from './api/apiClient';
import {
  ApiItemResponse,
  ApiListResponse,
  ApiOkResponse,
  ensureOk,
  unwrapItem,
  unwrapList,
} from './api/response';

import type { CreatedByFields } from '../models/CreatedBy';

export interface SiteMaterial extends CreatedByFields {
  id: number;
  site_project_id: number;
  /** 층 이름(예: B1) — 층별 자재 관리. 레거시(층 도입 전) 레코드는 비어 있을 수 있고 B1 취급. */
  floor_name?: string;
  name: string;
  quantity: number;
  vendor: string;
  category?: string;
  unit?: string;
  note?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateSiteMaterialRequest {
  name: string;
  quantity: number;
  /** 층 이름 — 생략 시 백엔드가 기본 B1 로 저장. */
  floor_name?: string;
  vendor?: string;
  category?: string;
  unit?: string;
  note?: string;
}

export interface UpdateSiteMaterialRequest {
  name?: string;
  quantity?: number;
  floor_name?: string;
  vendor?: string;
  category?: string;
  unit?: string;
  note?: string;
}

export interface SiteMaterialSummary {
  site_project_id: number;
  item_count: number;
  total_quantity: number;
  vendor_count: number;
  top: SiteMaterial[];
}

export class SiteMaterialService {
  static async list(projectId: number): Promise<SiteMaterial[]> {
    const resp = await apiClient.get<ApiListResponse<SiteMaterial>>(API_ENDPOINTS.SITE_MANAGEMENT.MATERIALS.LIST(projectId));
    return unwrapList(resp, '자재 목록 조회 실패');
  }

  static async get(materialId: number): Promise<SiteMaterial> {
    const resp = await apiClient.get<ApiItemResponse<SiteMaterial>>(API_ENDPOINTS.SITE_MANAGEMENT.MATERIALS.GET(materialId));
    return unwrapItem(resp, '자재 조회 실패');
  }

  static async create(projectId: number, data: CreateSiteMaterialRequest): Promise<SiteMaterial> {
    const resp = await apiClient.post<ApiItemResponse<SiteMaterial>>(API_ENDPOINTS.SITE_MANAGEMENT.MATERIALS.CREATE(projectId), data);
    return unwrapItem(resp, '자재 등록 실패');
  }

  static async update(materialId: number, data: UpdateSiteMaterialRequest): Promise<SiteMaterial> {
    const resp = await apiClient.put<ApiItemResponse<SiteMaterial>>(API_ENDPOINTS.SITE_MANAGEMENT.MATERIALS.UPDATE(materialId), data);
    return unwrapItem(resp, '자재 수정 실패');
  }

  static async delete(materialId: number): Promise<void> {
    const resp = await apiClient.delete<ApiOkResponse>(API_ENDPOINTS.SITE_MANAGEMENT.MATERIALS.DELETE(materialId));
    ensureOk(resp, '자재 삭제 실패');
  }

  /**
   * 자재 요약 — 개요(dashboard) 위젯 전용.
   * 종류 수 / 총 수량 / 고유 업체 수 + 수량 desc 상위 N (기본 5).
   */
  static async summary(projectId: number, top: number = 5): Promise<SiteMaterialSummary> {
    const resp = await apiClient.get<ApiItemResponse<SiteMaterialSummary>>(
      API_ENDPOINTS.SITE_MANAGEMENT.MATERIALS.SUMMARY(projectId),
      { params: { top } },
    );
    return unwrapItem(resp, '자재 요약 조회 실패');
  }
}
